import { useEffect, useMemo, useRef } from 'react';

import { useEntitlements } from '@/hooks/useEntitlements';
import { useMy } from '@/hooks/useMy';
import { useUpdateSetting } from '@/hooks/useSetting';
import { Setting } from '@/types';
import { appStorage } from '@/utils/storage/appStorage.util';
import {
  getTenantScopedKey,
  getTenantScopedValue,
} from '@/utils/storage/tenantScoping.util';

const SUGGESTION_PREFIX = 'integration-suggestion:';
const DISMISSED_SUGGESTIONS_KEY = 'chariot.dismissedIntegrationSuggestions';

/**
 * Fetches integration-suggestion Setting records from DynamoDB via /my and
 * returns a list of integration names the user hasn't configured.
 *
 * The correlation engine creates one Setting per unconfigured provider
 * (e.g. key "integration-suggestion:amazon", value true) when CanCorrelate
 * fires during batch correlation.  The user can dismiss via PUT /setting
 * which sets value to false.
 */
export function useIntegrationSuggestions() {
  const {
    data: settings,
    status,
    ...rest
  } = useMy(
    {
      resource: 'setting',
      query: SUGGESTION_PREFIX,
    },
    {
      // Only refetch periodically — suggestions don't change rapidly.
      refetchInterval: 5 * 60 * 1000,
    }
  );

  const { mutateAsync: updateSetting, isPending: isDismissing } =
    useUpdateSetting();

  // PUT /setting is gated on EntitlementManageSettings server-side; the
  // replay below must honor the same gate the Dismiss button does.
  const { canManageSettings, isLoading: entitlementsLoading } =
    useEntitlements();

  const suggestions = useMemo(() => {
    if (!settings || settings.length === 0) return [];

    return (settings as Setting[])
      .filter(
        s =>
          String(s.value) !== 'false' && s.name?.startsWith(SUGGESTION_PREFIX)
      )
      .map(s => s.name.slice(SUGGESTION_PREFIX.length));
  }, [settings]);

  /**
   * Persist a dismissal server-side.
   *
   * `value` MUST be the string 'false': a boolean false is dropped by the
   * backend's `dynamodbav:"value,omitempty"` marshaling, which would leave
   * the suggestion un-dismissed.
   */
  const dismiss = (name: string) =>
    updateSetting({
      name: `${SUGGESTION_PREFIX}${name}`,
      value: 'false',
      silent: true,
    });

  // Tracks the suggestions dataset a replay batch was started for, so one
  // dataset can never trigger duplicate PUT fan-outs across re-renders
  // (refetches return new array identities with the same content).
  const replayedDatasetRef = useRef<string | null>(null);

  // One-time replay of legacy localStorage dismissals into server settings.
  useEffect(() => {
    // MUST-4: fire only after this tenant's suggestions query succeeds —
    // never at module load or pre-auth.
    if (status !== 'success') return;
    // A user without manage-settings would 403 on every replay PUT, and the
    // catch below would clear the guard and retry on every query success.
    // Skip WITHOUT marking the guard ref: the stored list stays intact for a
    // session where the entitlement is granted (fail-closed while /whoami
    // loads — the effect re-runs once entitlements resolve).
    if (entitlementsLoading || !canManageSettings) return;
    // The tenant-scoped key is captured ONCE per replay and folded into the
    // guard key: two tenants with identical suggestion sets must not share a
    // guard entry (the second tenant's stored dismissals would never replay),
    // and the removal below must target the tenant the replay actually read
    // from, not whatever tenant is current when the PUTs resolve.
    const scopedKey = getTenantScopedKey(DISMISSED_SUGGESTIONS_KEY);
    const datasetKey = `${scopedKey}\u0000${suggestions.join('\u0000')}`;
    if (replayedDatasetRef.current === datasetKey) return;
    // MUST-1: getTenantScopedValue skips legacy migration for this key, so
    // another tenant's unscoped list can never be copied into this scope.
    const stored = getTenantScopedValue<string[]>(DISMISSED_SUGGESTIONS_KEY);
    // No scoped list -> nothing to replay. Deliberately do NOT mark the ref
    // and do NOT touch any keys (SHOULD-3b: other-tenant data stays intact).
    if (!Array.isArray(stored)) return;
    replayedDatasetRef.current = datasetKey;
    // MUST-2: PUT only names present in the live, server-originated
    // suggestion list — stale or injected names are never written.
    const names = stored.filter(name => suggestions.includes(name));
    Promise.all(
      names.map(name =>
        updateSetting({
          name: `${SUGGESTION_PREFIX}${name}`,
          value: 'false',
          silent: true,
        })
      )
    )
      .then(() => {
        // Remove only after ALL PUTs resolved, via the key captured when
        // the replay started — a tenant switch while the PUTs were in
        // flight must never delete the NEW tenant's stored list.
        appStorage.removeItem(scopedKey);
        // SHOULD-1: also drop the legacy unscoped key — its only writer is
        // deleted in this PR, so it can never be repopulated.
        appStorage.removeItem(DISMISSED_SUGGESTIONS_KEY);
      })
      .catch(() => {
        // SHOULD-3c: a rejected PUT leaves the scoped key in place; clearing
        // the guard lets the replay retry on the next query success.
        replayedDatasetRef.current = null;
      });
    // exhaustive-deps is OFF (React Compiler); updateSetting is deliberately
    // omitted — mutateAsync identity is not contractually stable.
  }, [status, suggestions, canManageSettings, entitlementsLoading]);

  return {
    ...rest,
    status,
    suggestions,
    dismiss,
    isDismissing,
  };
}
